import dayjs from 'dayjs';
import api from '../api/api';
import { KnowledgeBaseSearchResult } from '../../types/KnowledgeBaseSearchResult';
import { SimpleMessage } from '../../types/SimpleMessage';
import { KnowledgeBaseItemResponse } from '../../types/KnowledgeBaseItemResponse';

export interface KnowledgeBaseSearchParams {
  page: number;
  resultsPerPage: number;
  sortColumn: string;
  sortDirection: 'ASC' | 'DESC';
  query?: string;
  websiteSourceId?: number;
}

class KnowledgeBaseService {
  static async getKnowledgeBase(
    chatbotId: number,
    {
      page,
      resultsPerPage,
      sortColumn,
      sortDirection,
      query,
      websiteSourceId
    }: KnowledgeBaseSearchParams,
    signal?: AbortSignal
  ): Promise<KnowledgeBaseSearchResult> {
    const baseUrl = `chatbots/${chatbotId}/knowledge-base`;

    const searchParams = new URLSearchParams();
    if (query) {
      searchParams.append('query', query);
    }
    if (websiteSourceId !== undefined) {
      searchParams.append('websiteSourceId', websiteSourceId.toString());
    }
    searchParams.append('page', page.toString());
    searchParams.append('resultsPerPage', resultsPerPage.toString());
    searchParams.append('sortColumn', sortColumn);
    searchParams.append('sortDirection', sortDirection);

    const apiResponse = await api.get(`${baseUrl}?${searchParams}`, { signal });
    return apiResponse.data;
  }

  static async viewKnowledgeBaseItem(
    chatbotId: number,
    knowledgeBaseItemId: number
  ): Promise<{ filePreviewUrl: string; name: string; contentType: string }> {
    const response = await api.get(
      `chatbots/${chatbotId}/knowledge-base/${knowledgeBaseItemId}`,
      {
        responseType: 'blob'
      }
    );

    const filePreviewUrl = window.URL.createObjectURL(response.data);
    const contentDisposition = response.headers['content-disposition'];
    const contentType = response.headers['content-type'];
    const fileName = contentDisposition
      ? contentDisposition.split('filename=')[1].replace(/"/g, '')
      : 'downloaded-knowledgebase-item';

    return { filePreviewUrl, name: fileName, contentType };
  }

  static async uploadKnowledgeBaseItem(
    chatbotId: number,
    name: string,
    description: string | null,
    nextReviewDate: Date | null,
    file: File
  ): Promise<KnowledgeBaseItemResponse> {
    const formData = new FormData();

    const request = {
      chatbotId,
      name,
      description,
      nextReviewDate: nextReviewDate
        ? this.formatLocalDate(nextReviewDate)
        : null
    };

    formData.append(
      'request',
      new Blob([JSON.stringify(request)], { type: 'application/json' })
    );
    formData.append('item', file);

    const apiResponse = await api.post(
      `chatbots/${chatbotId}/knowledge-base`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return apiResponse.data;
  }

  static async updateKnowledgeBaseItem(
    chatbotId: number,
    documentId: number,
    name: string,
    description: string | null,
    nextReviewDate: Date | null,
    enabled: boolean
  ): Promise<KnowledgeBaseItemResponse> {
    const baseUrl = `chatbots/${chatbotId}/knowledge-base/${documentId}`;

    const request = {
      isPublic: false,
      name,
      description,
      nextReviewDate: nextReviewDate
        ? this.formatLocalDate(nextReviewDate)
        : null,
      enabled
    };

    const apiResponse = await api.put(baseUrl, request);
    return apiResponse.data;
  }

  static async setKnowledgeBaseItemEnabled(
    chatbotId: number,
    item: KnowledgeBaseItemResponse,
    enabled: boolean
  ): Promise<KnowledgeBaseItemResponse> {
    return this.updateKnowledgeBaseItem(
      chatbotId,
      item.id,
      item.name,
      item.description ?? null,
      item.nextReviewDate ? new Date(item.nextReviewDate) : null,
      enabled
    );
  }

  static async retryIngestion(
    chatbotId: number,
    documentId: number
  ): Promise<KnowledgeBaseItemResponse> {
    const apiResponse = await api.post(
      `chatbots/${chatbotId}/knowledge-base/${documentId}/retry-ingestion`
    );
    return apiResponse.data;
  }

  private static formatLocalDate(date: Date): string {
    return dayjs(date).format('YYYY-MM-DD');
  }

  static async deleteKnowledgeBaseItem(
    chatbotId: number,
    documentId: number
  ): Promise<SimpleMessage> {
    const baseUrl = `chatbots/${chatbotId}/knowledge-base/${documentId}`;
    const apiResponse = await api.delete(`${baseUrl}`);
    return apiResponse.data;
  }
}

export default KnowledgeBaseService;
